/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { memo, useCallback, useEffect, useRef, useState } from "react";

import ReactLoading from 'react-loading';
import CalendarCheckBox from "components/ui/checkbox/CalendarCheckBox";

import {
  calendarWrap,
  doublecircleIcon,
  impossibleIcon,
  saturday,
  sunday,
  weekDay,
  calendarArrow,
  calendarDisabled,
  CalenderloadingCss,
  loadingCalendarWrap,
} from "assets/css/shchMtmr";

import {
  chgDateToString,
  chgStringToDate,
  getWeekDayNm,
  isDisabledCalendarCell,
} from "utilities/date";

import { paddingZero } from "utilities/string";

import {
  initDateForPage,
} from "utilities/calendar.js";
import useGetLazyQuery from "hooks/useGetLazyQuery";
import { GET_CAPACITY_INFO } from "graphql/query";
import {useNavigate} from "react-router-dom";
import {useFormContext} from "react-hook-form";
import { isHoliday } from "@holiday-jp/holiday_jp";

/**
 * ご希望日時選択カレンダー
 * @param {Number} page ページ情報
 * @param {Object} kbuHmDayInfo カレンダー情報
 * @param {Setter} setKbuHmDayInfo カレンダー情報をセット
 */
const CalendarTable = memo(({ page, kbuHmDayInfo, setKbuHmDayInfo, addressKj, setIsActive, setIsOutOfServiceArea, setNoCapacity}) => {

  // 画面遷移用
  const navigate = useNavigate();

  // 住所コードから担当店舗の余力を取得
  const [getCapacityInfo, loading] = useGetLazyQuery(GET_CAPACITY_INFO, "no-cache");

  // 年月
  const [dateYearMonth, setDateYearMonth] = useState([]);
  // 日付(曜日)
  const [dateHeaders, setDateHeaders] = useState([]);
  // セル
  const [dataList, setDataList] = useState(null);

  const { setValue } = useFormContext();

  useEffect( () => {
    // // 余力なしで初期表示用データ作成
    // // 開始日・終了日を表示ページから算出
    let startDate = initDateForPage(page);
    const capacityList = createNoCapacityList(startDate);
    createDateYearMonth(capacityList);
    createDateHeaders(capacityList);
    createDateList(capacityList);
  }, []);

  // page値が変わるごとに初期表示
  const refFirstRef = useRef(true);
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      if (refFirstRef.current) {
        refFirstRef.current = false;
        return;
      }
    }
    (async () => {
      // 住所情報を生成
      await getCapacity();
  })()
  }, [page]);

  // カレンダーヘッダー年月リスト生成
  const createDateYearMonth = (capacityList) => {
    let columnsArray = [""];
    var tmpYmd = "";
    var colspan = 1;

    capacityList.forEach(item => {
       // カレンダー表示用年月文字列
       const strYm = item.targetYmd.slice(0,4) + "年" + item.targetYmd.slice(4,6) + "月";
       if (tmpYmd === strYm) {
        // 同一年月の場合、結合数を加算
        columnsArray[columnsArray.length - 1].colspan = (colspan += 1);
      } else {
        // 同一年月ではない場合、tmpYmdに年月を詰める
        item.targetYmd.slice(0,4) === "" ? tmpYmd = "" : tmpYmd = strYm;
        columnsArray.push({
          yearMonth: tmpYmd,
          colspan: 1,
        });
      }
    });
    setDateYearMonth(columnsArray)
  }

  // カレンダーヘッダー日付・曜日情報リスト生成
  const createDateHeaders = (capacityList) => {
    let columnsArray = [{ year: "", month: "", date: "", weekDay: "", holiday: "" }];
    capacityList.forEach(item => {
      // yyyyMMddをDate型に変換
      const date = chgStringToDate(item.targetYmd + "0000");
      columnsArray.push({
        year: date.getFullYear(),
        month: date.getMonth(),
        date: date.getDate(),
        weekDay: date.getDay(),
        holiday: isHoliday(date) ? "1" : "0"
      });
    });
    setDateHeaders(columnsArray);
  }

  // セル
  const createDateList = (capacityList) => {

    let dataList = [];
    let dataListForAm = [];
    let dataListFor1315 = [];
    let dataListFor1517 = [];

    capacityList.forEach(item => {
      dataListForAm.push({
        ymd: item.targetYmd,
        name: "cell",
        id: "time" + item.targetYmd + "_01",
        disabled: item.hasCapacityAM === 0 ? true: isDisabledCalendarCell(item.targetYmd, "01")
      });

      dataListFor1315.push({
        ymd: item.targetYmd,
        name: "cell",
        id: "time" + item.targetYmd + "_02",
        disabled: item.hasCapacity1315 === 0 ? true: isDisabledCalendarCell(item.targetYmd, "02")
      });

      dataListFor1517.push({
        ymd: item.targetYmd,
        name: "cell",
        id: "time" + item.targetYmd + "_03",
        disabled: item.hasCapacity1517 === 0 ? true: isDisabledCalendarCell(item.targetYmd, "03")
      });
    });

    // 順番に詰める
    dataList.push(dataListForAm);
    dataList.push(dataListFor1315);
    dataList.push(dataListFor1517);
    setDataList(dataList)
  }

  // カレンダーのセルを生成
  const createCalendarCell = (dataList, jkt) => {
    let columnsArray = [];
    if(dataList !== null) {
      dataList[jkt].forEach(item => {
        columnsArray.push({
          ymd: item.ymd,
          name: item.name,
          id: item.id,
          disabled: item.disabled,
        });
      });
    }
    return columnsArray;
  }

  // カレンダーを選択したら値を詰める
  const cellClick = (e) => {
    setKbuHmDayInfo({
      appointmentYmd: e.target.dataset.ymd,        // yyyyMMdd
      appointmentHmTimeCd: e.target.id.split('_')[1],   // hh00hh00(例：13001500)
      selectedPage: page,
    });

    // 〇箇所が選択された場合、余力不足の判定をfalseに設定
    if (e.target.id !== ""){
      setNoCapacity(false);
    }
  };

  // 指定日付から７日間の余力なしリストを生成
  const createNoCapacityList = (startYmd) => {
    // startYmdからendYmdまでのDate型オブジェクト
    let dateList = [];
    dateList.push(new Date(startYmd));
    for (let i = 1; i <= 6; i++) {
      let last_data = dateList[dateList.length -1];
      let date =new Date(last_data);
      date.setDate(date.getDate() + 1);
      dateList.push(date);
      // if (isDateMatch((endYmd) , date)) break;
    }
    // 各日付の余力無リストを作成
    const capacityList = [];
    dateList.forEach((ci) => {
      // 時間帯余力有無に「0」を設定
      const hasCapacityAM = 0;
      const hasCapacity1315 = 0;
      const hasCapacity1517 = 0;

      capacityList.push({
        targetYmd: chgDateToString(ci, ""),
        hasCapacityAM,
        hasCapacity1315,
        hasCapacity1517,
      });
    });

    return capacityList;
  }

  // 住所コードから担当店舗を取得する
  const getCapacity = useCallback(async () => {
    // 開始日・終了日を表示ページから算出
    let startDate = initDateForPage(page);
    let endDate = initDateForPage(page);
    endDate.setDate(endDate.getDate() + 6)

    // 住所コードから担当店舗を取得する
    const param = { input: {
        startYmd: chgDateToString(startDate, ""),
        sgyskAddrCd: addressKj,
        endYmd: chgDateToString(endDate, "")
      }
    };
    let errorCode = "";
    await getCapacityInfo(param,
      (data) => {
          // エラー存在チェック
          let err = data.errorResponse;
          if(!err) {
              // 余力情報が取得できる場合余力情報で、取得できない場合空情報でカレンダー生成
              const capacityList = data.capacityList ? data.capacityList : createNoCapacityList(startDate);
              createDateYearMonth(capacityList);
              createDateHeaders(capacityList);
              createDateList(capacityList);
              // カレンダー表示確認用にセット
              setValue("changeCapacityInfo", capacityList);
          } else {
            // エリア外判定又は問い合わせエラーの場合ネットワークエラー画面へ
            if (data.isOutOfArea || err.errorCd === 500) {
              // setIsOutOfServiceArea(true);
              errorCode = "408";
              return;
            } else {
              // エリア外以外のエラーの場合全ての日付を余力なしで表示
              const capacityList = createNoCapacityList(startDate);
              createDateYearMonth(capacityList);
              createDateHeaders(capacityList);
              createDateList(capacityList);
              // return;
            }
          }
      },
      (e) => {
        if ((e.message === "Response not successful: Received status code 500") ||
             (e.message === "Cannot convert undefined or null to object")){
          // ネットワークエラー画面へセット
          errorCode = "408";
            return;
          }
        },
        false
    );
    // エラーページへ遷移
    if (errorCode !== "") {
      navigate("/error", {
        state: { errorCode: errorCode },
      });
    }

    // loadingToFalse();
  }, [page]);

  return (
    <>
      <table css={loadingCalendarWrap}><tbody><tr><td>
      <table css={calendarWrap}>
        <thead>
          <tr>
            {dateYearMonth.map((item, index) =>
              <th key={index} colSpan={item.colspan} rowSpan={index === 0 ? "2" : ""}>
                {item.yearMonth}
              </th>
            )}
          </tr>
          <tr>
            {dateHeaders.map((item, index) => (
              index === 0
                ? ""
                : <th key={index} css={[item.holiday === "1" ? sunday : item.weekDay === 6 ? saturday : item.weekDay === 0 ? sunday : "", weekDay]}> {/** 土：saturday／日祝：sundayのCSSを付与※祝日最優先 */}
                  {item.date}
                  <span>{"(" + getWeekDayNm(String(item.year) + paddingZero(item.month + 1) + paddingZero(item.date)) + ")"}</span>
                </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <>
            <tr>
              <td>
                <span>9:00</span>
                <span css={calendarArrow}></span>
                <span>12:00</span>
              </td>
              {createCalendarCell(dataList, 0).map((item, index) => (
                <td
                  key={index}
                  css={item.disabled ? calendarDisabled : ""}
                  onClick={cellClick}
                >
                  <CalendarCheckBox id={item.id} name={item.name} ymd={item.ymd} disabled={item.disabled} kbuHmDayInfo={kbuHmDayInfo} page={page} setNoCapacity={setNoCapacity}>
                    {item.disabled
                      ? <span css={impossibleIcon} />
                      : <span css={doublecircleIcon} />}
                  </CalendarCheckBox>
                </td>
              ))}
            </tr>
            <tr>
              <td>13:00<span css={calendarArrow}></span>15:00</td>
              {createCalendarCell(dataList, 1).map((item, index) => (
                <td
                  key={index}
                  css={item.disabled ? calendarDisabled : ""}
                  onClick={cellClick}
                >
                  <CalendarCheckBox id={item.id} name={item.name} ymd={item.ymd} disabled={item.disabled} kbuHmDayInfo={kbuHmDayInfo} page={page} setNoCapacity={setNoCapacity}>
                    {item.disabled
                      ? <span css={impossibleIcon} />
                      : <span css={doublecircleIcon} />}
                  </CalendarCheckBox>
                </td>
              ))}
            </tr>
            <tr>
              <td>15:00<span css={calendarArrow}></span>17:00</td>
              {createCalendarCell(dataList, 2).map((item, index) => (
                <td
                  key={index}
                  css={item.disabled ? calendarDisabled : ""}
                  onClick={cellClick}
                >
                  <CalendarCheckBox id={item.id} name={item.name} ymd={item.ymd} disabled={item.disabled} kbuHmDayInfo={kbuHmDayInfo} page={page} setNoCapacity={setNoCapacity}>
                    {item.disabled
                      ? <span css={impossibleIcon} />
                      : <span css={doublecircleIcon} />}
                  </CalendarCheckBox>
                </td>
              ))}
            </tr>
          </>
        </tbody>
      </table>

      {/** ローディング */}
      {loading && (
          <div css={[CalenderloadingCss]}>
            <ReactLoading type="spin" color="#b5b5b5" height="50px" width="50px" />
          </div>
      )}
      </td></tr></tbody></table>

    </>
  );
});

export default CalendarTable;
